// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { GSGrid, SectionComponent, Pagination } from "@components";
import { compose } from "redux";
import type { State } from "@store/reducers";
import type { PaginationExtendedType } from "@types";
import { formatDateTime } from "@transformers/date";
import type {
  EmployeeAbsenceNotification,
  EmployeeAbsenceNotificationModal,
} from "@modules/shared/reducer/employee-absences/types";
import actionCreators from "@modules/shared/actionCreator";
import {
  getEmployeeAbsenceNotifications,
  getEmployeeAbsenceNotificationsPagination,
  getEmployeeAbsenceNotificationModal,
} from "@modules/shared/selectors/employee-absences";
import AbsenceDetailsNotificationContentModal from "../absence-details-notification-content-modal";
type OwnProps = $Exact<{
  id: string,
  clientId: number,
  employeeId: number,
  absenceId: number,
}>;

type PropsFromState = $Exact<{
  data: Array<EmployeeAbsenceNotification>,
  pagination: PaginationExtendedType,
  modal: EmployeeAbsenceNotificationModal,
}>;

type PropsFromDispatch = $Exact<{
  getAbsenceNotification: typeof actionCreators.GetEmployeeAbsenceNotification,
  getAbsenceNotifications: typeof actionCreators.GetEmployeeAbsenceNotifications,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type ComponentState = $Exact<{
  isSectionOpen: boolean,
}>;

export class AbsenceDetailsNotifications extends React.Component<Props, ComponentState> {
  state = {
    isSectionOpen: true,
  };

  componentDidMount() {
    this.props.getAbsenceNotifications({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      absenceId: this.props.absenceId,
    });
  }

  handlePaginationChange = (page: number) => {
    this.props.getAbsenceNotifications({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      absenceId: this.props.absenceId,
      page,
    });
  };

  showNotificationContent = (notification: EmployeeAbsenceNotification) => () => {
    this.props.getAbsenceNotification({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      absenceId: this.props.absenceId,
      notificationId: notification.id,
    });
  };

  get columns() {
    const res = [
      {
        key: "id",
        name: this.props.t("NOTIFICATION_ID"),
        width: "min-content",
      },
      {
        key: "method",
        name: this.props.t("METHOD"),
        renderCell: ({ row }: { row: EmployeeAbsenceNotification }) => this.props.t(row.method),
        width: "min-content",
        align: "left",
      },
      {
        key: "type",
        name: this.props.t("TYPE"),
        renderCell: ({ row }: { row: EmployeeAbsenceNotification }) =>
          this.props.t(row.type === "ABSENCE_ALERT" ? "ALERT" : row.type),
        width: "min-content",
        align: "left",
      },
      {
        key: "recipients",
        name: this.props.t("RECIPIENTS"),
        renderCell: ({ row }: { row: EmployeeAbsenceNotification }) => row.recipients.join(", "),
        align: "left",
      },
      {
        key: "dateSent",
        name: this.props.t("DATE_SENT"),
        renderCell: ({ row }: { row: EmployeeAbsenceNotification }) => formatDateTime(row.dateSent),
        width: "min-content",
      },
      {
        key: "view",
        name: this.props.t("VIEW"),
        width: "min-content",
        itemClassName: "justify-content-center",
        renderCell: ({ row }: { row: EmployeeAbsenceNotification }) =>
          !row.isArchived && (
            <span
              className="material-icons pointer font-size-26"
              onClick={this.showNotificationContent(row)}
            >
              search
            </span>
          ),
      },
    ];

    return res;
  }

  toggleSection = () => {
    this.setState({
      isSectionOpen: !this.state.isSectionOpen,
    });
  };

  render() {
    return (
      <SectionComponent
        id={this.props.id}
        title={<h5>{this.props.t("NOTIFICATIONS")}</h5>}
        wrapperClassName="flex-column"
        collapsable
        t={this.props.t}
        onTitleClick={this.toggleSection}
        isOpen={this.state.isSectionOpen}
      >
        <GSGrid rows={this.props.data} columns={this.columns} />
        <Pagination
          className="my-3 d-flex flex-fill justify-content-center"
          currentPage={this.props.pagination.pageNumber}
          totalPages={this.props.pagination.totalPages}
          onChange={this.handlePaginationChange}
        />
        {this.props.modal.visible && <AbsenceDetailsNotificationContentModal />}
      </SectionComponent>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  data: getEmployeeAbsenceNotifications(state),
  pagination: getEmployeeAbsenceNotificationsPagination(state),
  modal: getEmployeeAbsenceNotificationModal(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getAbsenceNotification: actionCreators.GetEmployeeAbsenceNotification,
  getAbsenceNotifications: actionCreators.GetEmployeeAbsenceNotifications,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AbsenceDetailsNotifications);
